import { selectActiveMediaDevices } from 'features/user-media/userMediaSlice';
import { ActiveMediaDevicePayload } from 'features/user-media/types';
import { store } from 'store/store';
import { MediaStreamType } from 'features/streaming/types';
import { stopStreamTracks } from 'features/user-media/utils';
import { compoundStream } from 'features/user-media/utils/compoundStream';
import { BasePublishing } from 'utils/webrtc/publishing';
import { DeviceSwitcher } from 'utils/webrtc/publishing/DeviceSwitcher';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { getErrorMessage, getErrorName, logger } from 'utils/logger';
import { isValidVBSelection } from 'utils/types';
import {
  emitPublishEvent,
  PUBLISH_VB_FALLBACK_EVENT,
  PUBLISH_VB_FALLBACK_MESSAGE,
} from 'utils/webrtc/publishing/publishTelemetry';
import { selectVirtualBackgroundSyncConfig } from 'features/virtual-backgrounds/selectors';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { PublishingOptions } from 'utils/webrtc/types';
import { defaultSimulcastMaxBitrates } from 'utils/webrtc/environment';
import { getDefaultVideoConstraints } from 'features/user-media/utils/getDefaultVideoConstraints';
import { getDefaultAudioConstraints } from 'features/user-media/utils/getDefaultAudioConstraints';
import { selectHDVideoSettings } from 'features/streaming/streamingSlice';
import { RTCClient } from 'utils/webrtc/index';

interface JoinMedia {
  audio: MediaStream | null;
  video: MediaStream | null;
}

export class PublishingFeed extends BasePublishing {
  joinMedia: JoinMedia = {
    audio: null,
    video: null,
  };

  constructor() {
    super('publishing');
  }

  private deviceSwitcher: DeviceSwitcher = new DeviceSwitcher(this, this.videoReconciler);

  changeAudioDevice = (device: ActiveMediaDevicePayload) =>
    this.deviceSwitcher.changeAudioDevice(device);

  changeVideoDeviceWithVB = (device: ActiveMediaDevicePayload) =>
    this.deviceSwitcher.changeVideoDeviceWithVB(device);

  changeVideoDevice = (device: ActiveMediaDevicePayload) =>
    this.deviceSwitcher.changeVideoDevice(device);

  getActiveMediaConfig = () => {
    const activeMediaDevices = selectActiveMediaDevices(store.getState());

    const videoEnabled = this.mediaStates.video.enabled;
    const audioEnabled = this.mediaStates.audio.enabled;

    return {
      video: videoEnabled
        ? {
            ...getDefaultVideoConstraints(),
            deviceId: {
              exact: activeMediaDevices.videoinput,
            },
          }
        : undefined,
      audio: audioEnabled
        ? {
            ...getDefaultAudioConstraints(),
            deviceId: {
              exact: activeMediaDevices.audioinput,
            },
          }
        : undefined,
    };
  };

  protected getSimulcastMaxBitrates = () => {
    const hdVideoSettings = selectHDVideoSettings(store.getState());

    return {
      ...defaultSimulcastMaxBitrates,
      high: hdVideoSettings.bitrate,
    };
  };

  protected getSendEncodings = () => {
    const hdVideoSettings = selectHDVideoSettings(store.getState());
    const bitrates = this.getSimulcastMaxBitrates();
    const encodings = {
      h: { rid: 'h', active: hdVideoSettings.enabled, maxBitrate: bitrates.high },
      m: {
        rid: 'm',
        active: true,
        maxBitrate: bitrates.medium,
        scaleResolutionDownBy: 2,
      },
      l: {
        rid: 'l',
        active: true,
        maxBitrate: bitrates.low,
        scaleResolutionDownBy: 4,
      },
    };

    return Object.values(encodings);
  };

  protected isSimulcastEnabled = () => true;

  setJoinMedia = (stream: MediaStream | null, kind: MediaStreamType) => {
    this.joinMedia[kind] = stream;
  };

  stopJoinMediaTracks = (kind: MediaStreamType) => {
    // Keeps the slot: callers install a replacement next, and their failure
    // paths still read the old reference.
    stopStreamTracks(this.joinMedia[kind]);
  };

  cleanupJoinMedia = (kind?: MediaStreamType) => {
    if (!kind) {
      Object.keys(this.joinMedia).forEach((key) => {
        const media = key as MediaStreamType;

        stopStreamTracks(this.joinMedia[media]);
        this.joinMedia[media] = null;
      });

      return;
    }

    stopStreamTracks(this.joinMedia[kind]);
    this.joinMedia[kind] = null;
  };

  getJoinPublishingOptions = async (): Promise<PublishingOptions> => {
    let stream = compoundStream(this.joinMedia.audio, this.joinMedia.video);
    if (!stream) {
      return {
        video: this.mediaStates.video.enabled,
        audio: this.mediaStates.audio.enabled,
      };
    }

    // This handles scenarios where the join screen is disabled and
    // the virtual background (VB) is configured via the SDK,
    // making it impossible to apply the VB on the join screen
    const syncVBConfig = selectVirtualBackgroundSyncConfig(store.getState());
    if (isValidVBSelection(syncVBConfig) && !VirtualBackgroundManager.activated) {
      stream = await this.applyVirtualBackground(stream, syncVBConfig);
    }

    return {
      video: !!stream.getVideoTracks().length,
      audio: !!stream.getAudioTracks().length,
      stream,
    };
  };

  getResetMediaStates = (preserve: boolean) => {
    if (this.joinMedia?.audio || this.joinMedia?.video) {
      return {
        preserveVideo: !!this.joinMedia.video,
        preserveAudio: !!this.joinMedia.audio,
      };
    }

    if (preserve) {
      return {
        preserveVideo: this.mediaStates.video.enabled,
        preserveAudio: this.mediaStates.audio.broadcasting, // cant use enabled since its always on, because of how we handle audio
      };
    }

    return {
      preserveVideo: this.defaultMediaState.enabled,
      preserveAudio: this.defaultMediaState.enabled,
    };
  };

  private async applyVirtualBackground(
    stream: MediaStream,
    config: SelectedVirtualBackground
  ): Promise<MediaStream> {
    if (stream.getVideoTracks().length > 0) {
      const audioTrack = stream.getAudioTracks()[0];
      try {
        const VBStream = await this.activateVirtualBackground(config, {
          stream,
        });
        if (VBStream) {
          // VB output is video-only; carry the mic across or the join publishes without it.
          if (audioTrack) {
            VBStream.addTrack(audioTrack);
          }
          return VBStream;
        }
      } catch (error) {
        emitPublishEvent({
          event: PUBLISH_VB_FALLBACK_EVENT,
          message: PUBLISH_VB_FALLBACK_MESSAGE,
          level: 'warn',
          feedType: this.kind,
          visibility: 'fleet',
          cap: 3,
          attributes: {
            errorName: getErrorName(error),
            errorMessage: getErrorMessage(error),
          },
        });
      }
    }
    return stream;
  }

  toggleSimulcastHDLayer = async () => {
    if (!this.handle) {
      return;
    }

    if (!this.getPeerConnection()) {
      this.negotiationMeta.pendingHDSimulcast = !this.negotiationMeta.pendingHDSimulcast;
      return;
    }

    this.toggleHDLayer();
  };

  onMediaState = async (medium: 'audio' | 'video', on: boolean, mid?: string) => {
    logger
      .remote()
      .debug(`Janus ${on ? 'started' : 'stopped'} receiving our ${medium} (mid=${mid})`);

    const feed = this.handle;

    if (!feed) {
      return;
    }

    feed.streams = feed.streams || {};

    if (on) {
      RTCClient.resetPublishOptions();

      if (mid) {
        if (medium === 'audio') {
          if (this.mediaStates.audio.broadcasting) {
            feed.videoroom.unmuteAudio(mid);
          } else {
            feed.videoroom.muteAudio(mid);
          }
          this.negotiationMeta.pendingAudioBroadcastState = null;
        }

        // Tripwire: a second live video sender while one is already announced is the double-publish
        // signature; mid resolution should prevent it, so a hit means a replay added an m-line.
        if (medium === 'video' && this.mediaStates.video.captured) {
          const announced = Object.values(feed.streams).find(
            (stream) => stream.type === 'video' && stream.on && stream.mid !== mid
          );
          if (announced) {
            logger
              .remote({ system: true, capture: 'streaming' })
              .warn('[publish] second live video mid while one already announced', {
                announced: announced.mid,
                incoming: mid,
              });
          }
        }

        feed.streams[mid] = { type: medium, mid, on: true };

        // Sender appeared while broadcasting is off (toggle/cold-publish raced it): stop it. Pass
        // `mid` explicitly — getVideoMid() can't see a not-yet-recorded mid.
        if (medium === 'video' && !this.mediaStates.video.broadcasting) {
          await this.videoReconciler.muteVideoTrack(mid);
        }

        if (medium === 'audio' && this.negotiationMeta.pendingAudioSignalingState) {
          this.resolvePendingAudioSignalingState();
        }
      }

      if (!feed.connected && !feed.connecting) {
        feed.connecting = true;
      }
    } else if (mid && feed.streams[mid]) {
      feed.streams[mid].on = false;
    }

    const shouldSendUpdate = on ? !this.mediaStates[medium].captured : false;

    if (mid && shouldSendUpdate) {
      const shouldBroadcast = on ? this.mediaStates[medium].broadcasting : true;

      if (shouldBroadcast) {
        this.signalingBus.addMessage(on ? 'start' : 'stop', feed.feedId, [{ type: medium, mid }]);
      }

      if (on) {
        this.mediaStates[medium].captured = true;

        if (medium === 'video') {
          this.videoReconciler.latchFromIntent();

          if (this.negotiationMeta.pendingHDSimulcast) {
            this.resolvePendingHDSimulcast();
          }
        }
      }
    }
  };

  private resolvePendingAudioSignalingState = () => {
    this.negotiationMeta.pendingAudioSignalingState = false;

    if (!this.handle) {
      return;
    }

    if (!this.mediaStates.audio.broadcasting) {
      return;
    }

    const audioStreams = Object.values(this.handle.streams).filter(
      (stream) => stream.type === 'audio'
    );

    if (audioStreams.length) {
      this.signalingBus.addMessage('start', this.handle.feedId, audioStreams);
    }
  };

  private resolvePendingHDSimulcast = () => {
    this.toggleHDLayer();

    this.negotiationMeta.pendingHDSimulcast = null;
  };

  private toggleHDLayer = () => {
    if (!this.handle) {
      return;
    }

    const peerConnection = this.getPeerConnection();
    if (!peerConnection) {
      return;
    }

    const videoSender = peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'video');

    if (videoSender) {
      const parameters = videoSender.getParameters();
      parameters.encodings[0].active = !parameters.encodings[0].active;

      logger
        .remote({ action: true })
        .log(
          `HD video toggled from ${parameters.encodings[0].active ? 'disabled' : 'enabled'} to ${
            parameters.encodings[0].active ? 'enabled' : 'disabled'
          }`
        );
      videoSender.setParameters(parameters);
    }
  };
}
